import { multipleLinkage, checkBooksQueue } from '../../api/books';
import { fetchNoteBuilds, createNoteBuilds, checkNoteBuildsQueue } from '../../api/noteBuilds';

export const ALLOWED_BUILD_TYPES = ['raw', 'archive', 'sample', 'm4b', 'audiogram'];
export const VISIBLE_TOAST_TYPES = ['error'];
export const DEFAULT_BUILDER = {
    name: null,
    schema: {},
    api: {
        ws: {}
    },
    handlers: {
        completed(event) {
            if (this.isQueued) return;
            this.transition({ type: 'DONE' }, event);
        },
        finished() {
            if (this.isQueued) return;
            this.transition({ type: 'FINISH' }, event);
        },
        failed(event) {
            if (this.isQueued) return;
            this.transition({ type: 'FAILED' }, event);
        },
        denied(event) {
            this.log({ title: 'Denied', ...event });
            // this.transition({ type: 'FAILED' }, event);
        },
        log(event) {
            this.log({ ...event });
        }
    },
    options: {
        configsLimit: 1,
        queueChecking: {
            delay: 10000,
            retries: 50
        }
    }
};
export const DEFAULT_PERMISSIONS = {
    build: true,
    build_in_process: true,
    builds: true,
    note_build: true,
    note_build_in_process: true,
    note_builds: true
};
export const DEFAULT_BUILD_OPTIONS = {
    without_title: false,
    without_author: false,
    without_preview: false,
    speed: 1.0
};
export const DEFAULT_BUILDER_PERMISSIONS = {
    fetch: 'builds',
    building: 'build',
    checking: 'build_in_process'
};
export const BACKGROUND_AUDIO_OPTIONS = {
    background_audio: null,
    background_volume: 0.3
};
export const SCHEMA = {
    providers: {
        required: true,
        multiple: false
    },
    build_types: {
        required: true,
        disabled: ({ providers }) => !providers,
        filter: ({ name }) => ALLOWED_BUILD_TYPES.includes(name)
    },
    speed: {
        required: true
    },
    without_title: {
        required: false
    },
    without_author: {
        required: false
    },
    without_preview: {
        required: false
    }
};
export const DEFAULT_STATE = 'idle';
export const FINITE_STATE = 'completed';
export const TRANSITIONS = {
    idle: {
        START: 'setup'
    },
    setup: {
        DONE: 'checking',
        FAILED: 'failed'
    },
    checking: {
        SKIP: 'fetching',
        FETCH: 'fetching',
        REPAIR: 'repairing',
        FAILED: 'failed',
        CANCEL: 'reset'
    },
    repairing: {
        DONE: 'fetching',
        FAILED: 'failed'
    },
    fetching: {
        DONE: 'ready',
        FAILED: 'failed'
    },
    ready: {
        START: 'checking',
        BUILDING: 'building',
        RESET: 'reset'
    },
    building: {
        QUEUED: 'queued',
        FAILED: 'failed'
    },
    queued: {
        DONE: 'completed',
        FAILED: 'failed',
        FINISH: 'reset'
    },
    completed: {
        DONE: 'fetching',
        FAILED: 'failed',
        RESET: 'reset'
    },
    finished: {
        RESET: 'reset'
    },
    failed: {
        RESET: 'reset'
    },
    reset: {
        IDLE: 'idle',
        READY: 'ready'
    }
};
export const BUILDERS = [
    {
        name: 'Audiobook',
        schema: [
            'providers',
            'build_types',
            'speed',
            'options',
            'without_author',
            'without_title',
            'without_preview'
        ],
        options: {
            configsLimit: 10
        },
        api: {
            ws: {
                name: 'linkage_book',
                validator({ id }) {
                    return id === this.bookId;
                }
            },
            fetch() {
                const { builds = [] } = this.book;
                return builds;
            },
            building(configs) {
                const data = {
                    build_configs: configs.map(config => {
                        const {
                            build_types = [],
                            speed,
                            without_author,
                            without_title,
                            without_preview
                        } = config;

                        return {
                            ...DEFAULT_BUILD_OPTIONS,
                            provider: config.providers,
                            build_types,
                            speed,
                            without_author,
                            without_title,
                            without_preview
                        };
                    })
                };
                return multipleLinkage(this.bookId, data);
            },
            checking() {
                return checkBooksQueue(this.bookId);
            }
        }
    },
    {
        name: 'Subtitles',
        schema: {},
        permissions: {
            fetch: 'note_builds',
            building: 'note_build',
            checking: 'note_build_in_process'
        },
        options: {
            configsLimit: 0
        },
        api: {
            ws: {
                name: 'complete_build_note',
                validator({ build_type }) {
                    return build_type === 'subtitles';
                }
            },
            async fetch() {
                const params = {
                    book: this.bookId,
                    build_type: 'subtitles'
                };
                const { results = [] } = await fetchNoteBuilds(params);
                return await results;
            },
            building() {
                const params = { build_type: 'subtitles' };
                return createNoteBuilds(this.bookId, params);
            },
            checking() {
                return checkNoteBuildsQueue(this.bookId);
            }
        }
    },
    {
        name: 'Metadata',
        schema: {
            providers: {
                multiple: true
            }
        },
        options: {
            configsLimit: 1
        },
        api: {
            ws: {
                name: 'linkage_book',
                validator({ id }) {
                    return id === this.bookId;
                }
            },
            building(configs) {
                const {
                    providers = [],
                    without_title,
                    without_author,
                    without_preview
                } = configs.shift();
                const data = {
                    build_configs: providers.map(provider => ({
                        ...DEFAULT_BUILD_OPTIONS,
                        build_types: ['meta_info'],
                        provider,
                        without_title,
                        without_author,
                        without_preview
                    }))
                };
                return multipleLinkage(this.bookId, data);
            },
            checking() {
                return checkBooksQueue(this.bookId);
            }
        }
    }
];
